import { Box, styled, Typography } from '@mui/material';
import * as Yup from 'yup';

import { TextFormField } from 'lib/form/fields/TextFormField';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { Tenant } from 'components/merchant/models/Tenant';
import { SettingDisplayText } from '../SettingDisplayText';

const PREFIX = 'AppVersionSettingsPrefix';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

const APP_VERSION_REGEX = new RegExp(/^\d+(?:\.\d+){0,3}$/);
const appVersionValidation = Yup.object().shape({
    ios: Yup.object().shape({
        must: Yup.string()
            .trim()
            .matches(APP_VERSION_REGEX, 'Please enter valid version format, e.g. 0.0.0.1130'),
        should: Yup.string()
            .trim()
            .matches(APP_VERSION_REGEX, 'Please enter valid version format, e.g. 0.0.0.1130')
    }),
    android: Yup.object().shape({
        must: Yup.string()
            .trim()
            .matches(APP_VERSION_REGEX, 'Please enter valid version format, e.g. 0.0.0.1130'),
        should: Yup.string()
            .trim()
            .matches(APP_VERSION_REGEX, 'Please enter valid version format, e.g. 0.0.0.1130')
    })
});

type IProp = {
    tenant: Tenant | null;
    onSubmit: (values: Tenant['appVersions']) => Promise<boolean>;
};

export const AppVersionSettings = ({ tenant, onSubmit }: IProp) => (
    <StyledMuiCardForm
        title="App Version"
        initialValues={{
            android: {
                must: tenant?.appVersions.android.must,
                should: tenant?.appVersions.android.should
            },
            ios: {
                must: tenant?.appVersions.ios.must,
                should: tenant?.appVersions.ios.should
            }
        }}
        childrenWhileDisplaying={
            <>
                <Box display="flex" flexDirection="column">
                    <Typography fontWeight={700}>IOS</Typography>
                    <Box marginTop={1}>
                        <SettingDisplayText title="Must Update" text={tenant?.appVersions.ios.must} />
                        <SettingDisplayText title="Should Update" text={tenant?.appVersions.ios.should} />
                    </Box>
                </Box>
                <Box marginTop={2} display="flex" flexDirection="column">
                    <Typography fontWeight={700}>Android</Typography>
                    <Box marginTop={1}>
                        <SettingDisplayText title="Must Update" text={tenant?.appVersions.android.must} />
                        <SettingDisplayText title="Should Update" text={tenant?.appVersions.android.should} />
                    </Box>
                </Box>
            </>
        }
        childrenWhileEditing={
            <>
                <Box display="flex" flexDirection="column">
                    <Typography fontWeight={700}>iOS</Typography>
                    <Box marginTop={1}>
                        <SettingDisplay
                            label="Must Update"
                            description="The user will be logged out and prompted to update their app to the latest version.
                            They will not be able to use the app until they have updated."
                        >
                            <TextFormField placeholder="e.g. 5.0.0" name="ios.must" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Should Update"
                            description="The user will be prompted to update their app.
                            They can continue to use the app without updating to the latest version."
                        >
                            <TextFormField placeholder="e.g. 5.0.0" name="ios.should" />
                        </SettingDisplay>
                    </Box>
                </Box>
                <Box marginTop={2} display="flex" flexDirection="column">
                    <Typography fontWeight={700}>Android</Typography>
                    <Box marginTop={1}>
                        <SettingDisplay
                            label="Must Update"
                            description="The user will be logged out and prompted to update their app to the latest version.
                            They will not be able to use the app until they have updated."
                        >
                            <TextFormField placeholder="e.g. 0.0.0.1130" name="android.must" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Should Update"
                            description="The user will be prompted to update their app.
                            They can continue to use the app without updating to the latest version."
                        >
                            <TextFormField placeholder="e.g. 0.0.0.1130" name="android.should" />
                        </SettingDisplay>
                    </Box>
                </Box>
            </>
        }
        validationSchema={appVersionValidation}
        onSubmit={onSubmit}
        className={classes.settingsCard}
        boxClassName={classes.displayClassName}
    />
);
